import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import Button from "../components/Button/Button";
import InputText from "../components/InputTextSlide/InputTextSlide";
import Header from "../elements/Header/Header";
import Loading from "../elements/LoadingScreen/Loading";
import { useStateValue } from "../stateProvider/stateProvider";
import { balansSort } from "../utils/utilities";
import {
  addSlideshowBlock,
  duplicateSlideshowBlockData,
  INITIAL_SLIDESHOW_BLOCKS,
  loadSlideshowFilters,
  removeSlideshowBlock,
  removeSlideshowBlockFile,
  SLIDESHOW_ACCEPTED_FILES,
  submitSlideshowBlocks,
  updateSlideshowBlockField,
  updateSlideshowBlockFiles,
} from "./slideshowController";
import "./Slideshow.css";

const SlideshowBlock = ({
  block,
  index,
  filtersData,
  onFieldChange,
  onFilesChange,
  onRemoveFile,
  onDuplicate,
  onDelete,
}) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      onFilesChange(index, acceptedFiles);
    },
    [index, onFilesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: SLIDESHOW_ACCEPTED_FILES,
  });

  return (
    <fieldset className="slideshow-block">
      <legend>Wizualizacja {index + 1}</legend>

      <div
        {...getRootProps()}
        className={`slideshow__dropzone ${isDragActive ? "slideshow__dropzone--active" : ""}`}
      >
        <input {...getInputProps()} />
        <p>Dodaj pliki png, jpg, gif, mp4, webm</p>
        <div>
          <div className="slideshow__files-preview">
            {block.files.map((file, fileIndex) => (
              <div key={`${file.name}-${fileIndex}`} className="slideshow__file-item">
                <button
                  type="button"
                  className="slideshow__file-remove-btn"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRemoveFile(index, fileIndex);
                  }}
                  aria-label={`Usun plik ${file.name}`}
                >
                  X
                </button>
                <img src={file.preview} alt={file.name} className="slideshow__file-preview" />
                <p className="slideshow__file-name">{file.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="slideshow-flex-row slideshow-flex-row--title">
        <h3 className="slideshow__subtitle">Uzupelnij dane do kampanii</h3>
      </div>

      <div className="slideshow-flex-row">
        <InputText
          placeholder="Kampania"
          value={block.kampania}
          text="Kampania: "
          name="kampania"
          handleChange={(event) => onFieldChange(index, "kampania", event.target.value)}
        />

        <InputText
          placeholder="Klient"
          value={block.klient}
          text="Klient: "
          name="klient"
          handleChange={(event) => onFieldChange(index, "klient", event.target.value)}
        />
      </div>

      <div className="slideshow-flex-row">
        {filtersData ? (
          <label className="input-text__wrapper input-text__label-top slideshow__select-label">
            Format:
            <select
              value={block.format}
              onChange={(event) => onFieldChange(index, "format", event.target.value)}
              className="login-form__input input-text slideshow__select-input"
              name="format"
            >
              <option value={""}></option>
              {balansSort(filtersData.Format).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <InputText
            placeholder="Format"
            value={block.format}
            text="Format: "
            name="format"
            handleChange={(event) => onFieldChange(index, "format", event.target.value)}
          />
        )}

        {filtersData ? (
          <label className="input-text__wrapper input-text__label-top slideshow__select-label">
            Branza:
            <select
              value={block.branza}
              onChange={(event) => onFieldChange(index, "branza", event.target.value)}
              className="login-form__input input-text slideshow__select-input"
              name="branza"
            >
              <option value={""}></option>
              {balansSort(filtersData.Kategoria).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <InputText
            placeholder="Branza"
            value={block.branza}
            text="Branza: "
            name="branza"
            handleChange={(event) => onFieldChange(index, "branza", event.target.value)}
          />
        )}
      </div>

      <div className="slideshow-flex-row">
        <label className="input-text__wrapper input-text__label-top slideshow__select-label">
          Rozmiar (opcjonalnie):
          <input
            type="text"
            value={block.copySize}
            onChange={(event) => onFieldChange(index, "copySize", event.target.value)}
            className="login-form__input input-text slideshow__select-input"
            name="copySize"
            placeholder="np. 300x250"
          />
          <small className="slideshow__optional-copy-size-note">
            Pole opcjonalne. Sluzy tylko do opisu w "Kopiuj wszystkie".
          </small>
        </label>
      </div>

      <div className="slideshow-block__actions">
        <button
          type="button"
          className="slideshow-block__btn slideshow-block__btn--duplicate"
          onClick={() => onDuplicate(block.reactId)}
        >
          Duplikuj
        </button>
        <button
          type="button"
          className="slideshow-block__btn slideshow-block__btn--delete"
          onClick={() => onDelete(block.reactId)}
        >
          Usun
        </button>
      </div>
    </fieldset>
  );
};

const Slideshow = () => {
  const [reducer, dispatch] = useStateValue();
  const [blocks, setBlocks] = useState(INITIAL_SLIDESHOW_BLOCKS);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyAllText, setCopyAllText] = useState("Kopiuj wszystkie");
  const [copiedResultId, setCopiedResultId] = useState("");

  const getSizeLabelFromFormat = (format = "") => {
    const match = format.match(/(\d{2,5}\s*[xX]\s*\d{2,5})/);
    if (!match) return format || "?x?";
    return match[1].replace(/\s+/g, "").toLowerCase();
  };

  const copySingleLink = async (result) => {
    await navigator.clipboard.writeText(result.link);
    setCopiedResultId(result.id);

    setTimeout(() => {
      setCopiedResultId("");
    }, 1200);
  };

  const copyAllLinks = async () => {
    const formattedLines = results.map(
      (result) =>
        `${result.kampania+" | "+ getSizeLabelFromFormat(result.format) +  (result.copySize ? " | " +result.copySize.trim():"")}: ${result.link}`
    );
    await navigator.clipboard.writeText(formattedLines.join("\n"));
    setCopyAllText("Skopiowano");

    setTimeout(() => {
      setCopyAllText("Kopiuj wszystkie");
    }, 1500);
  };

  const handleFieldChange = (blockIndex, field, value) => {
    setBlocks((previous) => updateSlideshowBlockField(previous, blockIndex, field, value));
  };

  const handleFilesChange = (blockIndex, fileList) => {
    setBlocks((previous) => updateSlideshowBlockFiles(previous, blockIndex, fileList));
  };

  const handleRemoveFile = (blockIndex, fileIndex) => {
    setBlocks((previous) => removeSlideshowBlockFile(previous, blockIndex, fileIndex));
  };

  const handleDuplicateBlock = (reactId) => {
    setBlocks((previous) => duplicateSlideshowBlockData(previous, reactId));
  };

  const handleDeleteBlock = (reactId) => {
    setBlocks((previous) => removeSlideshowBlock(previous, reactId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await submitSlideshowBlocks({
      blocks,
      setLoading,
      setResults,
      setBlocks,
    });
  };

  useEffect(() => {
    loadSlideshowFilters({
      choosenTable: reducer.choosenTable,
      dispatch,
    });
  }, [reducer.choosenTable, dispatch]);

  return (
    <>
      <Header />
      {loading && (
        <Loading
          info={"Prosze czekac, dodajemy wizualizacje"}
          styles={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: "0",
            left: "0",
          }}
        />
      )}

      <div className="slideshow">
        <form className="slideshow__form-wrapper" onSubmit={handleSubmit}>
          {blocks.map((block, index) => (
            <SlideshowBlock
              key={block.reactId}
              block={block}
              index={index}
              filtersData={reducer.filtersData}
              onFieldChange={handleFieldChange}
              onFilesChange={handleFilesChange}
              onRemoveFile={handleRemoveFile}
              onDuplicate={handleDuplicateBlock}
              onDelete={handleDeleteBlock}
            />
          ))}

          <div className="slideshow__actions">
            <Button
              txt="Nowy"
              click={() => setBlocks((previous) => addSlideshowBlock(previous))}
              styles={{
                minWidth: "160px",
                margin: "0",
                backgroundColor: "black",
              }}
            />
            <Button
              txt="Wyslij wszystko"
              click={() => {}}
              type="submit"
              styles={{
                minWidth: "220px",
                margin: "0",
                backgroundColor: "var(--higlight-color)",
              }}
            />
          </div>
        </form>
      </div>

      {results.length > 0 && (
        <div className="slideshow__result-modal">
          <div className="slideshow__result-panel">
            <div className="slideshow__result-panel-header">
              <h3>Wygenerowane linki</h3>
              <button
                type="button"
                className="slideshow__result-close"
                onClick={() => setResults([])}
              >
                Zamknij
              </button>
            </div>

            <div className="slideshow__result-list">
              {results.map((result, index) => (
                <div key={`${result.id}-${index}`} className="slideshow__result-row">
                  <div className="slideshow__result-meta">
                    <span className="slideshow__result-label">
                      {result.kampania} | {result.format}
                    </span>
                    {Boolean((result.copySize || "").trim()) && (
                      <span className="slideshow__result-size">
                        Rozmiar: {result.copySize}
                      </span>
                    )}
                    <a href={result.link} target="_blank" rel="noreferrer">
                      {result.link}
                    </a>
                  </div>
                  <button
                    type="button"
                    className="slideshow__result-copy-btn"
                    onClick={() => copySingleLink(result)}
                  >
                    {copiedResultId === result.id ? "Skopiowano" : "Kopiuj"}
                  </button>
                </div>
              ))}
            </div>

            <div className="slideshow__result-footer">
              <button
                type="button"
                className="slideshow__result-copy-all-btn"
                onClick={copyAllLinks}
              >
                {copyAllText}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Slideshow;
